import React, { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import { connectWs, sendWs } from "../services/wsClient";

const AUDIENCE_OPTIONS = ["Owner", "Tenant", "Executive", "All"];

const emptyOption = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  text: ""
});

function isApiSuccess(data) {
  return data?.success === true || data?.status === "success";
}

function EVotingCreate() {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userId = useMemo(
    () =>
      localStorage.getItem("userid") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("id") ||
      "",
    []
  );

  const role = useMemo(
    () =>
      String(localStorage.getItem("role") || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, ""),
    []
  );
  const isTenant = role === "TENANT";
  const isOwner = role === "OWNER";
  const isAdmin = role === "ADMIN" || role === "SUPERADMIN";

  React.useEffect(() => {
    if (isTenant || isOwner) {
      toast.error("You are not allowed to create polls");
      navigate("/e-voting", { replace: true });
    }
  }, [isTenant, isOwner, navigate]);

  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState([emptyOption(), emptyOption()]);
  const [audience, setAudience] = useState("All");
  const [allowMultipleAnswers, setAllowMultipleAnswers] = useState(false);
  const [setStartTime, setSetStartTime] = useState(false);
  const [startTimeLocal, setStartTimeLocal] = useState("");
  const [setEndTime, setSetEndTime] = useState(false);
  const [endTimeLocal, setEndTimeLocal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const updateOption = (id, text) => {
    setOptions((prev) => prev.map((row) => (row.id === id ? { ...row, text } : row)));
  };

  const addOption = () => {
    setOptions((prev) => [...prev, emptyOption()]);
  };

  const removeOption = (id) => {
    setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((row) => row.id !== id)));
  };

  const onDragStart = (index) => setDragIndex(index);

  const onDragOver = (event, index) => {
    event.preventDefault();
    if (dragIndex == null || dragIndex === index) return;
    setOptions((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(index);
  };

  const onDragEnd = () => setDragIndex(null);

  const reset = () => {
    setQuestion("");
    setOptions([emptyOption(), emptyOption()]);
    setAudience("All");
    setAllowMultipleAnswers(false);
    setSetStartTime(false);
    setStartTimeLocal("");
    setSetEndTime(false);
    setEndTimeLocal("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing user id. Please login again.");
      return;
    }

    const trimmedQuestion = question.trim();
    const cleanedOptions = options
      .map((row) => String(row.text || "").trim())
      .filter(Boolean);

    if (!trimmedQuestion) {
      toast.error("Question is required");
      return;
    }
    if (cleanedOptions.length < 2) {
      toast.error("Add at least 2 non-empty options");
      return;
    }
    if (!AUDIENCE_OPTIONS.includes(audience)) {
      toast.error("Choose an audience");
      return;
    }
    if (setEndTime && !endTimeLocal) {
      toast.error("End time is required when Set end time is on");
      return;
    }
    if (setStartTime && !startTimeLocal) {
      toast.error("Start time is required when Set start time is on");
      return;
    }
    if (setStartTime && setEndTime && startTimeLocal && endTimeLocal) {
      if (new Date(startTimeLocal) >= new Date(endTimeLocal)) {
        toast.error("Start time must be before end time");
        return;
      }
    }
    if (setStartTime && startTimeLocal && new Date(startTimeLocal) <= new Date()) {
      toast.error("Start time must be in the future");
      return;
    }

    const payload = {
      action: "eVoting.create",
      userid: userId,
      question: trimmedQuestion,
      options: cleanedOptions,
      audience,
      allowMultipleAnswers: Boolean(allowMultipleAnswers),
      setStartTime: Boolean(setStartTime),
      setEndTime: Boolean(setEndTime)
    };
    if (setStartTime) {
      payload.startTime = new Date(startTimeLocal).toISOString();
    }
    if (setEndTime) {
      payload.endTime = new Date(endTimeLocal).toISOString();
    }

    setSubmitting(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(API_BASE_URL, payload);
      const data = response?.data;
      if (!isApiSuccess(data)) {
        toast.error(data?.message || data?.error || "Failed to create poll");
        return;
      }
      toast.success(data?.message || "Poll created successfully");
      const createdId =
        data?.pollId ||
        data?.id ||
        data?.data?._id ||
        data?.data?.id ||
        data?.poll?._id ||
        data?.poll?.id ||
        data?.poll?.eVotingId;

      // Live notification to the selected audience over the socket.
      const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;
      const userRole = String(localStorage.getItem("role") || "user").trim() || "user";
      if (createdId && WS_URL && userId) {
        connectWs({ baseUrl: WS_URL, userId, userType: userRole })
          .then(() => {
            sendWs({
              action: "poll_created",
              type: "notification",
              userId,
              userType: userRole,
              eVotingId: createdId,
              audience: data?.audience ?? audience,
              poll: data?.poll || null,
              recipientIds: Array.isArray(data?.recipientIds) ? data.recipientIds : null
            });
          })
          .catch(() => {});
      }

      if (createdId) {
        navigate(`/e-voting-view/${createdId}`);
      } else {
        navigate("/e-voting");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to create poll"
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setSubmitting(false);
    }
  };

  if (isTenant || isOwner) {
    return null;
  }

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <h1 className="m-0 txt_lft">Create poll</h1>
            <p style={{ margin: 0 }}>
              Admin or an active executive can create polls. Audience controls who sees each poll.
            </p>
          </div>
          <NavLink to="/e-voting" className="btn btn-secondary">
            Back
          </NavLink>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title mb-0">Poll details</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label className="form-label" htmlFor="evoting-question">
                        Question <span className="required-star">*</span>
                      </label>
                      <input
                        id="evoting-question"
                        type="text"
                        className="form-control"
                        placeholder="Ask question"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        maxLength={300}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label" htmlFor="evoting-audience">
                        Audience <span className="required-star">*</span>
                      </label>
                      <select
                        id="evoting-audience"
                        className="form-control"
                        value={audience}
                        onChange={(e) => setAudience(e.target.value)}
                        required
                      >
                        {AUDIENCE_OPTIONS.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                      <small className="form-text text-muted">
                        Owner, Tenant, Executive, or All
                      </small>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">
                      Options <span className="required-star">*</span>
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={addOption}
                    >
                      <i className="fas fa-plus mr-1" aria-hidden />
                      Add option
                    </button>
                  </div>

                  {options.map((row, index) => (
                    <div
                      key={row.id}
                      className="d-flex align-items-center mb-2"
                      style={{ gap: 8, opacity: dragIndex === index ? 0.7 : 1 }}
                      draggable
                      onDragStart={() => onDragStart(index)}
                      onDragOver={(e) => onDragOver(e, index)}
                      onDragEnd={onDragEnd}
                    >
                      <span
                        className="text-muted"
                        title="Drag to reorder"
                        style={{ cursor: "grab", minWidth: 18 }}
                      >
                        <i className="fas fa-bars" aria-hidden />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Option ${index + 1}`}
                        value={row.text}
                        onChange={(e) => updateOption(row.id, e.target.value)}
                        maxLength={120}
                      />
                      {options.length > 2 ? (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          aria-label="Remove option"
                          onClick={() => removeOption(row.id)}
                        >
                          <i className="fas fa-trash" aria-hidden />
                        </button>
                      ) : null}
                    </div>
                  ))}
                  <small className="form-text text-muted">
                    At least 2 non-empty options are required.
                  </small>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="evoting-multiple"
                          checked={allowMultipleAnswers}
                          onChange={(e) => setAllowMultipleAnswers(e.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor="evoting-multiple">
                          Allow multiple answers
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="evoting-start-time-toggle"
                          checked={setStartTime}
                          onChange={(e) => {
                            setSetStartTime(e.target.checked);
                            if (!e.target.checked) setStartTimeLocal("");
                          }}
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="evoting-start-time-toggle"
                        >
                          Set start time
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="evoting-end-time-toggle"
                          checked={setEndTime}
                          onChange={(e) => setSetEndTime(e.target.checked)}
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="evoting-end-time-toggle"
                        >
                          Set end time
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {(setStartTime || setEndTime) ? (
                  <div className="row">
                    {setStartTime ? (
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="evoting-start-time">
                            Start time <span className="required-star">*</span>
                          </label>
                          <input
                            id="evoting-start-time"
                            type="datetime-local"
                            className="form-control"
                            value={startTimeLocal}
                            onChange={(e) => setStartTimeLocal(e.target.value)}
                            required={setStartTime}
                          />
                          <small className="form-text text-muted">
                            Poll will be "Scheduled" until this time, then go live automatically.
                          </small>
                        </div>
                      </div>
                    ) : null}
                    {setEndTime ? (
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="evoting-end-time">
                            End time <span className="required-star">*</span>
                          </label>
                          <input
                            id="evoting-end-time"
                            type="datetime-local"
                            className="form-control"
                            value={endTimeLocal}
                            onChange={(e) => setEndTimeLocal(e.target.value)}
                            required={setEndTime}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div
                  className="d-flex"
                  style={{ gap: 12, justifyContent: "flex-end", marginTop: 12 }}
                >
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? "Creating..." : "Create poll"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={reset}
                    disabled={submitting}
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EVotingCreate;
